import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check, Zap, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const PricingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const plans = [
    {
      name: 'Starter',
      price: '$10',
      period: '/month',
      description: 'Perfect for getting started with document automation.',
      credits: '100 credits',
      features: [
        'Up to 100 documents/month',
        'All document types',
        'REST API access',
        'Email support',
        '7-day data retention',
      ],
      cta: 'Get Started',
      featured: false,
    },
    {
      name: 'Pro',
      price: '$49',
      period: '/month',
      description: 'For growing teams and businesses with higher volume.',
      credits: '1,000 credits',
      features: [
        'Up to 1,000 documents/month',
        'All document types',
        'REST API + Webhooks',
        'Priority support',
        '30-day data retention',
        'Batch processing',
        'Custom extraction rules',
      ],
      cta: 'Start Free Trial',
      featured: true,
    },
    {
      name: 'Scale',
      price: 'Custom',
      period: '',
      description: 'Enterprise-grade solution for high-volume processing.',
      credits: 'Unlimited',
      features: [
        'Unlimited documents',
        'All document types',
        'Full API access',
        'Dedicated support',
        'Custom retention',
        'SLA guarantee',
        'On-premise deployment',
        'Custom integrations',
      ],
      cta: 'Contact Sales',
      featured: false,
    },
  ];

  return (
    <section
      ref={ref}
      id="pricing"
      className="py-20 md:py-28 bg-white"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-[#635BFF]/10 text-[#635BFF] text-sm font-medium rounded-full mb-4">
            Pricing
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A2540] mb-6">
            Simple, Credit-Based Pricing
          </h2>
          <p className="text-lg text-[#425466] leading-relaxed">
            Pay only for what you use. Each processed document consumes one credit. No hidden fees.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className={`relative rounded-3xl p-8 ${
                plan.featured
                  ? 'bg-gradient-to-br from-[#635BFF] to-[#7C3AED] text-white shadow-xl shadow-[#635BFF]/25'
                  : 'bg-white border border-gray-100 hover:border-[#635BFF]/30 hover:shadow-xl'
              } transition-all duration-300`}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
            >
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-4 py-1.5 bg-[#00D4FF] text-[#0A2540] text-sm font-semibold rounded-full">
                    <Zap className="w-4 h-4" />
                    Most Popular
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h3 className={`font-heading text-xl font-semibold ${plan.featured ? 'text-white' : 'text-[#0A2540]'} mb-2`}>
                  {plan.name}
                </h3>
                <p className={`text-sm ${plan.featured ? 'text-white/80' : 'text-[#969DA3]'}`}>
                  {plan.description}
                </p>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className={`font-heading text-4xl font-bold ${plan.featured ? 'text-white' : 'text-[#0A2540]'}`}>
                    {plan.price}
                  </span>
                  <span className={`text-sm ${plan.featured ? 'text-white/70' : 'text-[#969DA3]'}`}>
                    {plan.period}
                  </span>
                </div>
                <p className={`text-sm mt-1 ${plan.featured ? 'text-white/80' : 'text-[#635BFF]'} font-medium`}>
                  {plan.credits}
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      plan.featured ? 'bg-white/20' : 'bg-[#635BFF]/10'
                    }`}>
                      <Check className={`w-3 h-3 ${plan.featured ? 'text-white' : 'text-[#635BFF]'}`} />
                    </div>
                    <span className={`text-sm ${plan.featured ? 'text-white/90' : 'text-[#425466]'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                className={`w-full rounded-full h-12 font-semibold transition-all duration-200 active:scale-95 ${
                  plan.featured
                    ? 'bg-white text-[#635BFF] hover:bg-white/90'
                    : 'bg-[#635BFF] text-white hover:bg-[#5851E0]'
                }`}
                data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
              >
                {plan.cta}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <p className="text-[#969DA3] text-sm">
            All plans include a 14-day free trial. No credit card required.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
